import { compareVersions, type AppVersionInfo } from './appVersion';

// Pure reading of Firestore appConfig/update — the single source of truth for
// "which build is current" (see AppUpdatePrompt.tsx for the field list). Kept
// free of React/Firebase so the update prompt, the feedback survey's version
// gate and tests share exactly one decision.

export const DEFAULT_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.triptribe.mobile';
export const DEFAULT_APP_STORE_URL = 'https://apps.apple.com/in/app/tonyte/id6810779018';
export const APP_STORE_ID = '6810779018';
export const ANDROID_PACKAGE = 'com.triptribe.mobile';

export type UpdateLevel = 'recommended' | 'required';
type RawConfig = Record<string, unknown>;

export function toConfigNumber(value: unknown, fallback = 0): number {
  const numberValue = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(numberValue) ? numberValue : fallback;
}

export function toConfigString(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

/** Pure decision: which update prompt (if any) this install should see. */
export function decideUpdatePrompt(
  config: RawConfig,
  installed: Pick<AppVersionInfo, 'platform' | 'versionCode' | 'versionName'>,
): { level: UpdateLevel; target: string; targetLabel: string } | null {
  if (config.active !== true) return null;
  if (installed.platform === 'android') {
    const latest = toConfigNumber(config.latestAndroidVersionCode);
    const minimum = toConfigNumber(config.minimumRequiredAndroidVersionCode);
    const label = toConfigString(config.latestAndroidVersionName);
    if (minimum > 0 && installed.versionCode < minimum) return { level: 'required', target: String(Math.max(latest, minimum)), targetLabel: label };
    if (latest > 0 && installed.versionCode < latest) return { level: 'recommended', target: String(latest), targetLabel: label };
    return null;
  }
  if (installed.platform === 'ios') {
    const latest = toConfigString(config.latestIosVersion);
    const minimum = toConfigString(config.minimumSupportedIosVersion);
    if (!installed.versionName) return null;
    if (minimum && compareVersions(installed.versionName, minimum) < 0) return { level: 'required', target: latest || minimum, targetLabel: latest || minimum };
    if (latest && compareVersions(installed.versionName, latest) < 0) return { level: 'recommended', target: latest, targetLabel: latest };
  }
  return null;
}

export function storeUrlFor(config: RawConfig | null, platform: string): string {
  return platform === 'ios'
    ? toConfigString(config?.appStoreUrl, DEFAULT_APP_STORE_URL)
    : toConfigString(config?.playStoreUrl, DEFAULT_PLAY_STORE_URL);
}

/**
 * Store page for leaving a review. iOS: the product page with
 * `action=write-review` (Apple's documented persistent-link pattern);
 * Android: the Play listing (Google: send people to the store rather than
 * triggering the in-app review API from a button).
 */
export function storeReviewUrlFor(platform: string): string | null {
  if (platform === 'ios') return `https://apps.apple.com/app/id${APP_STORE_ID}?action=write-review`;
  if (platform === 'android') return `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}&showAllReviews=true`;
  return null;
}
